import type { Dispatch, SetStateAction } from 'react';
import type { ArrowDirection } from '../components/atomic/buttons/arrows/Arrows';
import type { GiantIconName } from '../components/atomic/giants/giant_icons/GiantIcon';
import type { NavBarTab } from '../components/composite/nav/nav_bar/NavBar';
import type { InfoPopupState } from '../shared/infoPopup/infoPopupState';
import {
  createVerticalArrowPositions,
  FIGMA_VERTICAL_ARROW_SCALE,
} from '../shared/verticalArrows/verticalArrows';
import type { DeliveryCardItemFrame } from './delivery_card/DeliveryCard';
import type { DeliveryHistoryEntry, GiantId } from '../../../domain';

export type DeliveryListStatusBanner = 'rating' | 'failed';

export type DeliveryListScreenProps = {
  activeTab?: NavBarTab;
  onInfoClick?: () => void;
  onNavigate?: (direction: ArrowDirection) => void;
  onTabChange?: (tab: NavBarTab) => void;
  onReplayDelivery?: (giantId: GiantId, orderIndex: number) => void;
  onOpenDetails?: (giantId: GiantId, orderIndex: number) => void;
};

export const LIST_ARROW_SCALE = FIGMA_VERTICAL_ARROW_SCALE;
export const LIST_ARROW_POSITIONS = createVerticalArrowPositions(346);

export type DeliveryListScreenState = InfoPopupState & {
  deliveries: DeliveryHistoryEntry[];
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  againOpen: boolean;
  setAgainOpen: Dispatch<SetStateAction<boolean>>;
  canReplay: boolean;
};

export type DeliveryListScreenModel = {
  hasDeliveries: boolean;
  giantId: GiantIconName;
  orderIndex: number;
  statusBanner: DeliveryListStatusBanner;
  itemFrame: DeliveryCardItemFrame;
  /** Successful deliveries reveal the item; failed ones stay sealed for replay. */
  revealItem: boolean;
  levelNameText: string;
  giantNameText: string;
  deliveryRating: number | null;
  deliveryCount: number;
  canNavigateUp: boolean;
  canNavigateDown: boolean;
  infoOpen: boolean;
  setInfoOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  againOpen: boolean;
  setAgainOpen: Dispatch<SetStateAction<boolean>>;
  canReplay: boolean;
  activeTab: NavBarTab;
  onInfoClick?: () => void;
  onNavigate?: (direction: ArrowDirection) => void;
  onTabChange?: (tab: NavBarTab) => void;
  onReplayDelivery?: (giantId: GiantId, orderIndex: number) => void;
  onOpenDetails?: (giantId: GiantId, orderIndex: number) => void;
};

function clampSelectedIndex(index: number, count: number): number {
  if (count <= 0) {
    return 0;
  }
  return Math.min(Math.max(0, index), count - 1);
}

export function createDeliveryListScreenModel(
  props: DeliveryListScreenProps,
  state: DeliveryListScreenState,
  content: {
    giantId: GiantIconName;
    orderIndex: number;
    levelNameText: string;
    giantNameText: string;
    statusBanner: DeliveryListStatusBanner;
    itemFrame: DeliveryCardItemFrame;
    revealItem: boolean;
    deliveryRating: number | null;
  },
): DeliveryListScreenModel {
  const count = state.deliveries.length;
  const selectedIndex = clampSelectedIndex(state.selectedIndex, count);
  const overlayOpen = state.infoOpen || state.againOpen;

  return {
    hasDeliveries: count > 0,
    giantId: content.giantId,
    orderIndex: content.orderIndex,
    statusBanner: content.statusBanner,
    itemFrame: content.itemFrame,
    revealItem: content.revealItem,
    levelNameText: content.levelNameText,
    giantNameText: content.giantNameText,
    deliveryRating: content.deliveryRating,
    deliveryCount: count,
    canNavigateUp: count > 0 && selectedIndex > 0 && !overlayOpen,
    canNavigateDown: count > 0 && selectedIndex < count - 1 && !overlayOpen,
    infoOpen: state.infoOpen,
    setInfoOpen: state.setInfoOpen,
    setSelectedIndex: state.setSelectedIndex,
    againOpen: state.againOpen,
    setAgainOpen: state.setAgainOpen,
    canReplay: state.canReplay,
    activeTab: props.activeTab ?? 'deliveries',
    onInfoClick: props.onInfoClick,
    onNavigate: props.onNavigate,
    onTabChange: props.onTabChange,
    onReplayDelivery: props.onReplayDelivery,
    onOpenDetails: props.onOpenDetails,
  };
}
